/**
 * Color Constants
 *
 * Centralized color definitions for the Transportation Safety Platform.
 * All colors should be imported from this file to ensure consistency.
 *
 * Usage:
 * ```typescript
 * import { COLORS, BASIC_COLORS, STATUS_COLORS } from '../constants/colors';
 *
 * const buttonStyle = { background: COLORS.primary.DEFAULT };
 * const chartColor = BASIC_COLORS['Unsafe Driving'];
 * const statusStyle = STATUS_COLORS.excellent;
 * ```
 */

// ============================================================================
// PRIMARY PALETTE
// ============================================================================

/**
 * Main color palette - matches CSS variables in modern.css
 */
export const COLORS = {
  primary: {
    DEFAULT: '#2563eb',
    light: '#dbeafe',
    dark: '#1e40af',
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  success: {
    DEFAULT: '#10b981',
    light: '#d1fae5',
    dark: '#065f46',
    50: '#ecfdf5',
    100: '#d1fae5',
    500: '#10b981',
    600: '#059669',
    900: '#064e3b',
  },
  warning: {
    DEFAULT: '#f59e0b',
    light: '#fef3c7',
    dark: '#92400e',
    50: '#fffbeb',
    100: '#fef3c7',
    500: '#f59e0b',
    600: '#d97706',
    900: '#78350f',
  },
  danger: {
    DEFAULT: '#ef4444',
    light: '#fee2e2',
    dark: '#991b1b',
    50: '#fef2f2',
    100: '#fee2e2',
    500: '#ef4444',
    600: '#dc2626',
    900: '#7f1d1d',
  },
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
} as const;

// ============================================================================
// BASIC CATEGORY COLORS
// ============================================================================

/**
 * FMCSA BASIC category colors - used across analytics components
 * Consistent color scheme for all 7 BASIC categories
 */
export const BASIC_COLORS: Record<string, string> = {
  // BASIC-11: Unsafe Driving
  'Unsafe Driving': '#ef4444',
  'BASIC-11': '#ef4444',

  // BASIC-12: HOS Compliance
  'Hours of Service': '#f59e0b',
  'HOS Compliance': '#f59e0b',
  'BASIC-12': '#f59e0b',

  // BASIC-13: Driver Fitness
  'Driver Fitness': '#8b5cf6',
  'BASIC-13': '#8b5cf6',

  // BASIC-14: Controlled Substances/Alcohol
  'Controlled Substances': '#ec4899',
  'Controlled Substances/Alcohol': '#ec4899',
  'BASIC-14': '#ec4899',

  // BASIC-15: Vehicle Maintenance
  'Vehicle Maintenance': '#3b82f6',
  'BASIC-15': '#3b82f6',

  // BASIC-16: Crash Indicator
  'Crash Indicator': '#dc2626',
  'BASIC-16': '#dc2626',

  // BASIC-17: Hazardous Materials
  'Hazardous Materials': '#f97316',
  'Hazmat Compliance': '#f97316',
  'HM Compliance': '#f97316',
  'BASIC-17': '#f97316',

  // Fallback
  'Other': '#6b7280',
};

// ============================================================================
// CHART COLORS
// ============================================================================

/**
 * Chart color palette for data visualizations
 * Use for pie charts, bar charts, line charts, etc.
 */
export const CHART_COLORS = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#10b981', // green
  '#f59e0b', // amber
  '#8b5cf6', // purple
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
  '#84cc16', // lime
  '#14b8a6', // teal
] as const;

/**
 * Chart color scheme for time series (multiple lines)
 */
export const TIME_SERIES_COLORS = {
  violations: '#ef4444',
  inspections: '#3b82f6',
  crashes: '#f59e0b',
  oos: '#dc2626',
  clean: '#10b981',
} as const;

// ============================================================================
// STATUS COLORS
// ============================================================================

/**
 * Status colors for BASIC scores and risk levels
 * Provides background, text, and border colors for consistency
 */
export const STATUS_COLORS = {
  excellent: {
    bg: '#d1fae5',
    text: '#065f46',
    border: '#10b981',
  },
  good: {
    bg: '#d1fae5',
    text: '#065f46',
    border: '#10b981',
  },
  safe: {
    bg: '#dbeafe',
    text: '#1e40af',
    border: '#3b82f6',
  },
  warning: {
    bg: '#fef3c7',
    text: '#92400e',
    border: '#f59e0b',
  },
  critical: {
    bg: '#fee2e2',
    text: '#991b1b',
    border: '#ef4444',
  },
  insufficient: {
    bg: '#f3f4f6',
    text: '#4b5563',
    border: '#9ca3af',
  },
  eliminated: {
    bg: '#f3f4f6',
    text: '#6b7280',
    border: '#d1d5db',
  },
} as const;

/**
 * Risk level colors for merger analysis
 */
export const RISK_COLORS = {
  LOW: {
    bg: '#d1fae5',
    text: '#065f46',
    badge: '#10b981',
  },
  MEDIUM: {
    bg: '#fef3c7',
    text: '#92400e',
    badge: '#f59e0b',
  },
  HIGH: {
    bg: '#fee2e2',
    text: '#991b1b',
    badge: '#ef4444',
  },
  CRITICAL: {
    bg: '#fef2f2',
    text: '#7f1d1d',
    badge: '#991b1b',
  },
} as const;

/**
 * Recommendation colors
 */
export const RECOMMENDATION_COLORS = {
  APPROVE: {
    bg: '#d1fae5',
    text: '#065f46',
    badge: '#10b981',
  },
  REVIEW: {
    bg: '#fef3c7',
    text: '#92400e',
    badge: '#f59e0b',
  },
  REJECT: {
    bg: '#fee2e2',
    text: '#991b1b',
    badge: '#ef4444',
  },
} as const;

// ============================================================================
// SEVERITY COLORS
// ============================================================================

/**
 * Violation severity colors
 */
export const SEVERITY_COLORS: Record<string, string> = {
  high: '#ef4444',
  medium: '#f59e0b',
  low: '#10b981',
  '10': '#ef4444',
  '8': '#f97316',
  '7': '#f59e0b',
  '5': '#eab308',
  '4': '#84cc16',
  '3': '#22c55e',
  '1': '#10b981',
} as const;

// ============================================================================
// SAFETY RATING COLORS
// ============================================================================

/**
 * FMCSA safety rating colors
 */
export const SAFETY_RATING_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  S: { bg: '#d1fae5', text: '#065f46', border: '#10b981' }, // Satisfactory
  C: { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' }, // Conditional
  U: { bg: '#fee2e2', text: '#991b1b', border: '#ef4444' }, // Unsatisfactory
  'Not Rated': { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' },
} as const;

// ============================================================================
// DATA SOURCE COLORS
// ============================================================================

/**
 * Colors for data source indicators
 */
export const DATA_SOURCE_COLORS = {
  official: {
    bg: '#1e40af',
    text: '#ffffff',
    badge: '#dbeafe',
  },
  calculated: {
    bg: '#6b7280',
    text: '#ffffff',
    badge: '#f3f4f6',
  },
  monitored: {
    bg: '#10b981',
    text: '#ffffff',
    badge: '#d1fae5',
  },
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get status color by status name
 */
export const getStatusColor = (status: string): { bg: string; text: string; border: string } => {
  const normalized = status.toLowerCase();
  return STATUS_COLORS[normalized as keyof typeof STATUS_COLORS] || STATUS_COLORS.insufficient;
};

/**
 * Get BASIC category color by name or ID
 */
export const getBasicColor = (category: string): string => {
  return BASIC_COLORS[category] || BASIC_COLORS.Other;
};

/**
 * Get risk level color
 */
export const getRiskColor = (level: string): (typeof RISK_COLORS)[keyof typeof RISK_COLORS] => {
  return RISK_COLORS[level as keyof typeof RISK_COLORS] || RISK_COLORS.MEDIUM;
};

/**
 * Get recommendation color
 */
export const getRecommendationColor = (rec: string): (typeof RECOMMENDATION_COLORS)[keyof typeof RECOMMENDATION_COLORS] => {
  return RECOMMENDATION_COLORS[rec as keyof typeof RECOMMENDATION_COLORS] || RECOMMENDATION_COLORS.REVIEW;
};

export default COLORS;
